// NOTE: R.B we are choosing to load firebase auth using initializeAuth isntead of getAuth because of performance issues on mobile with auths iframe.
// See... https://github.com/firebase/firebase-js-sdk/issues/4946#issuecomment-1163262324 and https://firebase.google.com/docs/auth/web/custom-dependencies
// The side effect of this is that we do not automatically load dependencies which support signInWithPopup or signInWithRedirect
// This is why it is faster and the iframe is not loaded. If we need these in the future refer to the links above for how to use those.
import { 
  initializeAuth, 
  EmailAuthProvider, 
  // reauthenticateWithCredential,
  multiFactor, 
  getMultiFactorResolver, 
  TotpMultiFactorGenerator, 
  indexedDBLocalPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence, 
  signInWithCredential,
  signOut,
  signInWithCustomToken
} from 'firebase/auth';

import { getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

import userAPI from './api/user'

const AUTH_UTIL = {
  auth: null,
  appCheck: null,
  init: function (app) {
    AUTH_UTIL.auth = initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence
      ],
    });

    AUTH_UTIL.initAppCheck(app)
  },
  initAppCheck: function (app) {
    // Create a ReCaptchaEnterpriseProvider instance using your reCAPTCHA Enterprise
    // site key and pass it to initializeAppCheck().
    if (!AUTH_UTIL.appCheck && process.env.VUE_APP_RECAPTCHA_ENTERPRISE_KEY) {
      // console.log(`Init ReCaptchaEnterpriseProvider`)

      // Init debug provider if we are in dev
      if (process.env.NODE_ENV === 'development') {
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }

      AUTH_UTIL.appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(process.env.VUE_APP_RECAPTCHA_ENTERPRISE_KEY),
        isTokenAutoRefreshEnabled: true // Set to true to allow auto-refresh.
      });
    }
  },
  getCurrenUserIdToken: async function () {
    return AUTH_UTIL.auth.currentUser && (await AUTH_UTIL.auth.currentUser.getIdToken())
  },
  getAppCheckToken: async function () {
    try {
      let appCheckToken;
      if (AUTH_UTIL.appCheck) {
        const getTokenResponse = await getToken(AUTH_UTIL.appCheck, /* forceRefresh= */ false);
        if (getTokenResponse) {
          appCheckToken = getTokenResponse.token
        }
      }
      return appCheckToken
    } catch (err) {
        // Handle any errors if the token was not retrieved.
        // console.log(`Error getting app check token ${err}`)
        return;
    }
  },
  // Multi Factor Authentication (MFA) functions
  /**
   * Check if the current user has multi factor authentication enabled
   * @returns {boolean} True if the current user has multi factor authentication enabled, false if not or auth is not initialized.
   */
  multiFactorEnabled: function () {
    const multiFactorUser = multiFactor(AUTH_UTIL.auth.currentUser);
    return multiFactorUser.enrolledFactors.length > 0;
  },
  getMultiFactorResolver: function (error) {
    if (AUTH_UTIL.auth) {
      return getMultiFactorResolver(AUTH_UTIL.auth, error);
    } else {
      return null;
    }
  },
  generateTOTPSecret: async function () {
    if (AUTH_UTIL.auth && AUTH_UTIL.auth.currentUser) {
      const multiFactorUser = multiFactor(AUTH_UTIL.auth.currentUser);
      const multiFactorSession = await multiFactorUser.getSession();
      return await TotpMultiFactorGenerator.generateSecret(
        multiFactorSession
      );
    } else {
      return null;
    }
  },
  enrollTOTP: async function (totpSecret, otpFromAuthenticator) {
    if (AUTH_UTIL.auth && AUTH_UTIL.auth.currentUser && totpSecret) {
      const multiFactorUser = multiFactor(AUTH_UTIL.auth.currentUser);
      const totpAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        otpFromAuthenticator
      );
      try {
        await multiFactorUser.enroll(
          totpAssertion,
          'Pikle' // Display name for the authenticator app
        );
        return true;
      } catch (error) {
        // Enrollment failed
        // console.log(`Error enrolling TOTP ${error.code} message ${error.message}`);
        return false;
      }
    } else {
      return false;
    }
  },
  /**
   * Signs the user in with their email and password to confirm their identity and then unenrolls the TOTP second factor with the given factorId.
   * 
   * @param {*} factorId
   * @param {*} email 
   * @param {*} password 
   * @returns Returns true if unenrollment and reauth was successful, false if not or auth is not initialized.
   */
  unEnrollTOTP: async function (email, password, unEnrolCode, userUID, oneTimeToken) {
    if (AUTH_UTIL.auth && AUTH_UTIL.auth.currentUser) {
      signOut(AUTH_UTIL.auth);
    }
    if (AUTH_UTIL.auth && unEnrolCode && oneTimeToken) {
      // console.log(`unEnrollTOTP unEnrolCode ${unEnrolCode} email ${email} password ${password ? '***' : ''}`)
      
      const credential = EmailAuthProvider.credential(email, password);
      // See if the credential is valid by signing in with it and catch the mfa error.
      let canUnenroll = false;
      let multiFactorInfo = null;
      try {
        // console.log(`Signing in with credential to check it is valid...`)
        await signInWithCredential(AUTH_UTIL.auth, credential);
      } catch (error) {
        // console.log(`Caught error signing in with credential to check it is valid`)
        if (error.code === 'auth/multi-factor-auth-required') {
          // console.log(`MFA required error caught, can unenroll`)
          // console.log(`error ${JSON.stringify(error)}`)
          // console.log('USER UID', userUID);
          const uidMatches = userUID === error.customData?._serverResponse?.localId;
          const loginEmailMatches = email === error.customData?._serverResponse?.email;
          // console.log('UID MATCHES?', uidMatches);
          // console.log('EMAIL MATCHES?', loginEmailMatches);

          if (uidMatches && loginEmailMatches) {
            // Get a custom token for the UID and sign in with that to prove we own the account.
            const customToken = await userAPI.getCustomTokenForUID(userUID, oneTimeToken);
            if (customToken) {
              await signInWithCustomToken(AUTH_UTIL.auth, customToken);
            }
          }

          // We can unenroll the user from TOTP if we get the mfa required error.
          if (AUTH_UTIL.auth.currentUser) {
            multiFactorInfo = multiFactor(AUTH_UTIL.auth.currentUser).enrolledFactors.find(
                (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID
            );
            // Check the unEnrolCode is current.
            const validCode = await userAPI.validateOneTimeCode(unEnrolCode)
            // console.log(`multiFactorInfo ${multiFactorInfo.factorId} validCode ${validCode}`)
            if (multiFactorInfo && validCode) {
              canUnenroll = true;
            }
          }
        }
      }

      if (canUnenroll) {
        // Unenroll from TOTP MFA.
        console.log(`Can start unenrolling from TOTP...`)
        
        try {
          await multiFactor(AUTH_UTIL.auth.currentUser).unenroll(multiFactorInfo);

          console.log(`Sign out and returning...`)
          // Sign the user out
          await signOut(AUTH_UTIL.auth);
          return true;
        } catch  (error) {
          if (error.code === 'auth/user-token-expired') {
            console.log(`User signed out...`)
            // Sign the user out
            // await signOut(AUTH_UTIL.auth);
            return true;
          }
        }
      }
    }
    console.log(`Returning false...`)
    return false;
  },
  /**
   * Sign in using multi factor authentication with TOTP
   * @param {string} otpFromAuthenticator The OTP from the user's authenticator app
   * @returns {Promise<boolean|undefined>} Returns true if sign-in was successful, false if the OTP was invalid or expired, or undefined if TOTP is not an available second factor or auth is not initialized.
   */
  signInMultiFactorAuth: async function (otpFromAuthenticator, multiFactorResolver) {
    if (AUTH_UTIL.auth && multiFactorResolver) {
      // Check if TOTP is one of the available second factors.
      const indexOfTotpFactor = multiFactorResolver.hints.findIndex(
          (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (indexOfTotpFactor === -1) {
          // TOTP is not one of the available second factors.
          return false;
      }
      // Ask the user for the OTP from their authenticator app and create a
      // TotpMultiFactorAssertion to complete sign-in.
      const multiFactorAssertion =
          TotpMultiFactorGenerator.assertionForSignIn(
              multiFactorResolver.hints[indexOfTotpFactor].uid,
              otpFromAuthenticator
          );
      try {
          return await multiFactorResolver.resolveSignIn(
              multiFactorAssertion
          );
          // Successfully signed in!
      } catch (error) {
          // Invalid or expired OTP.
          return false;
      }
    }
  }
}

export default AUTH_UTIL
